/**
 * Gifting product page — custom behaviour for the gifting template.
 *
 * Everything is scoped to `[data-gifting-root]` so it never touches the
 * normal product page. The variant radios live inside the native Shopify
 * product form, so line-item properties and the selected variant id are
 * submitted through the standard add-to-cart flow (product-form.js).
 */
(function () {
  'use strict';

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function giftingStrings() {
    const s = window.variantStrings || {};
    return {
      addToCart: s.addToCart || 'Add to cart',
      soldOut: s.soldOut || 'Sold out',
      unavailable: s.unavailable || 'Unavailable',
    };
  }

  function initAccordions(root) {
    root.querySelectorAll('[data-gifting-card]').forEach((card) => {
      const trigger = card.querySelector('[data-gifting-accordion-trigger]');
      const panel = card.querySelector('[data-gifting-accordion-panel]');
      if (!trigger || !panel) return;

      const setOpen = (isOpen) => {
        card.classList.toggle('is-open', isOpen);
        trigger.setAttribute('aria-expanded', String(isOpen));
        panel.setAttribute('aria-hidden', String(!isOpen));
        panel.inert = !isOpen;
      };

      setOpen(false);
      trigger.addEventListener('click', () => {
        setOpen(!card.classList.contains('is-open'));
      });
    });
  }

  function initCounters(root) {
    root.querySelectorAll('[data-gifting-counter]').forEach((input) => {
      const update = () => {
        const count = input.closest('.gifting-field')?.querySelector('[data-gifting-count]');
        if (count) count.textContent = String(input.value.length);
      };
      input.addEventListener('input', update);
      update();
    });
  }

  function initThumbnails(root) {
    const mainImage = root.querySelector('[data-gifting-main-img]');
    if (!mainImage) return;

    root.querySelectorAll('[data-gifting-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        setActiveMedia(root, thumb.dataset.giftingThumb);
        mainImage.src = thumb.dataset.giftingThumbUrl;
        mainImage.srcset = thumb.dataset.giftingThumbSrcset || mainImage.srcset;
      });
    });
  }

  function setActiveMedia(root, mediaId) {
    root.querySelectorAll('[data-gifting-thumb]').forEach((thumb) => {
      const isActive = String(thumb.dataset.giftingThumb) === String(mediaId);
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  function initGiftCardToggle(root) {
    root.querySelectorAll('[data-gifting-card-toggle]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const card = input.closest('[data-gifting-card]');
        const message = card.querySelector('[data-gifting-card-message]');
        if (!message) return;
        const textarea = message.querySelector('textarea');
        const show = input.value === 'Yes';
        message.hidden = !show;
        if (textarea) textarea.disabled = !show;
      });
    });
  }

  function getVariants(root) {
    const script = root.querySelector('#gifting-product-variants');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Gifting product: invalid variants JSON', e);
      return [];
    }
  }

  function getSelectedVariant(root, variants) {
    const checked = Array.from(root.querySelectorAll('[data-gifting-option]:checked'));
    const selectedValues = checked.map((input) => input.value);
    if (!selectedValues.length) return variants[0] || null;
    return (
      variants.find((variant) =>
        selectedValues.every((value, index) => variant.options[index] === value)
      ) || null
    );
  }

  function updatePrice(root, variant) {
    const priceEl = root.querySelector('.gifting-product__price');
    if (!priceEl) return;

    const priceNode = priceEl.querySelector('.price');
    const regularPrice = priceEl.querySelector('.price__regular .price-item--regular');
    const salePrice = priceEl.querySelector('.price__sale .price-item--sale');
    const comparePrice = priceEl.querySelector('.price__sale .price-item--regular');
    const saleBadge = priceEl.querySelector('.price__badge-sale');
    const soldOutBadge = priceEl.querySelector('.price__badge-sold-out');

    const onSale = !!(variant && variant.compare_at_price);

    priceNode?.classList.toggle('price--on-sale', onSale);
    priceNode?.classList.toggle('price--sold-out', !!variant && !variant.available);

    if (regularPrice) regularPrice.textContent = variant ? variant.price : '';
    if (salePrice) salePrice.textContent = variant ? variant.price : '';
    if (comparePrice) comparePrice.textContent = onSale ? variant.compare_at_price : '';
    if (saleBadge) saleBadge.hidden = !onSale;
    if (soldOutBadge) soldOutBadge.hidden = !!(variant && variant.available);
  }

  function updateMedia(root, variant) {
    const mainImage = root.querySelector('[data-gifting-main-img]');
    if (mainImage && variant && variant.media_id) {
      const thumb = root.querySelector(`[data-gifting-thumb="${variant.media_id}"]`);
      if (thumb) {
        mainImage.src = thumb.dataset.giftingThumbUrl;
        mainImage.srcset = thumb.dataset.giftingThumbSrcset || mainImage.srcset;
        setActiveMedia(root, variant.media_id);
      }
    }
  }

  function updateSubmitButton(root, variant) {
    const strings = giftingStrings();
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    const submit = root.querySelector('[data-gifting-submit]');
    const idInput = form?.querySelector('input[name="id"]');
    const checkout = root.querySelector('.gifting-product__dynamic-checkout');

    const available = !!variant && variant.available;
    const label = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;

    if (idInput) {
      idInput.value = variant ? String(variant.id) : '';
      idInput.disabled = !available;
    }

    if (submit) {
      submit.disabled = !available;
      if (available) {
        submit.removeAttribute('aria-disabled');
      } else {
        submit.setAttribute('aria-disabled', 'true');
      }
      const labelSpan = submit.querySelector('span');
      if (labelSpan) labelSpan.textContent = label;
    }

    if (checkout) checkout.hidden = !available;
  }

  function initVariants(root) {
    const variants = getVariants(root);
    const optionInputs = root.querySelectorAll('[data-gifting-option]');
    if (!optionInputs.length) return;

    const update = () => {
      const variant = getSelectedVariant(root, variants);
      updatePrice(root, variant);
      updateMedia(root, variant);
      updateSubmitButton(root, variant);
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', update);
    });

    update();
  }

  onReady(() => {
    document.querySelectorAll('[data-gifting-root]').forEach((root) => {
      initAccordions(root);
      initCounters(root);
      initThumbnails(root);
      initGiftCardToggle(root);
      initVariants(root);
    });
  });
})();
